/**
 * Выбор способа подъёма и доступности — в КАРТОЧКЕ цели (запрос владельца
 * 31.07): человек решает «как мне подниматься» до нажатия «Маршрут», а не
 * после, когда линия уже нарисована не так.
 *
 * ДВЕ РАЗНЫЕ ВЕЩИ, И ОНИ РАЗДЕЛЕНЫ НАМЕРЕННО:
 *
 *  * СПОСОБ ПОДЪЁМА (лифт / эскалатор / лестница) — предпочтение, вес в графе.
 *    Не запрет: если выбранного нет на пути, маршрут всё равно построится, но
 *    предпочтённый перевешивает крюк до ~250 метров.
 *  * ДОСТУПНОСТЬ («без лестниц»: инвалидная или детская коляска) — профиль,
 *    то есть запрет: лестницы и эскалаторы исключаются, входы со ступенями
 *    обходятся. Для роутера оба значка — одно ограничение: детскую коляску на
 *    эскалатор не пускают правила большинства центров, и вести туда нельзя.
 *
 * ПРОТИВОРЕЧИЕ РЕШАЕТ ЧЕЛОВЕК: явный выбор «Лестница» при включённом «без
 *  лестниц» снимает режим — и это видно (значок гаснет), а не молча.
 *
 * ТОЛЬКО ЗНАЧКИ, без подписей и рамки (владелец, 31.07): карточка цели — не
 * форма настроек, ряд подписей забивал её текстом. Названия остаются в
 * aria-label и всплывающей подсказке — для скринридера и для «а что это?».
 */
import { tr } from '@app/i18n'
import { useEffect, useState } from 'preact/hooks'
import { setAccessMode, setConnectorPref } from '../actions'
import { accessMode, type ConnectorPref, langTick, prefNoAltTick, routePref, venue } from '../state'
import {
  GLYPH_ESCALATOR,
  GLYPH_LIFT,
  GLYPH_STAIRS,
  GLYPH_STROLLER,
  GLYPH_WHEELCHAIR,
} from './glyphs'

const CLIMBS: readonly { pref: ConnectorPref; glyph: string; label: string }[] = [
  { pref: 'lift', glyph: GLYPH_LIFT, label: 'connector.lift' },
  { pref: 'escalator', glyph: GLYPH_ESCALATOR, label: 'connector.escalator' },
  { pref: 'stairs', glyph: GLYPH_STAIRS, label: 'connector.stairs' },
]

export function ClimbPicker() {
  langTick.value
  const v = venue.value

  // На одноэтажной площадке подниматься некуда — не показываем ничего.
  if (!v || v.levels.length < 2) return null
  const pref = routePref.value
  const access = accessMode.value

  return (
    <fieldset class="climb-row" aria-label={tr('connector.row_label')}>
      {CLIMBS.map((c) => (
        <button
          key={c.pref}
          type="button"
          class={pref === c.pref ? 'climb-btn climb-on' : 'climb-btn'}
          aria-pressed={pref === c.pref}
          aria-label={tr(c.label as never)}
          title={tr(c.label as never)}
          // Повторный клик снимает предпочтение — возврат к «как короче».
          onClick={() => setConnectorPref(pref === c.pref ? null : c.pref)}
        >
          <span class="glyph" aria-hidden="true" dangerouslySetInnerHTML={{ __html: c.glyph }} />
        </button>
      ))}
      <span class="climb-sep" aria-hidden="true" />
      <button
        type="button"
        class={access === 'wheelchair' ? 'climb-btn climb-on' : 'climb-btn'}
        aria-pressed={access === 'wheelchair'}
        aria-label={tr('connector.wheelchair')}
        title={tr('connector.wheelchair')}
        onClick={() => setAccessMode(access === 'wheelchair' ? 'walk' : 'wheelchair')}
      >
        <span
          class="glyph"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: GLYPH_WHEELCHAIR }}
        />
      </button>
      <button
        type="button"
        class={access === 'stroller' ? 'climb-btn climb-on' : 'climb-btn'}
        aria-pressed={access === 'stroller'}
        aria-label={tr('connector.stroller')}
        title={tr('connector.stroller')}
        onClick={() => setAccessMode(access === 'stroller' ? 'walk' : 'stroller')}
      >
        <span
          class="glyph"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: GLYPH_STROLLER }}
        />
      </button>
    </fieldset>
  )
}

/** Отдельным блоком под рядом значков, чтобы не дёргать их ширину. */
export function ClimbNoAltHint() {
  langTick.value
  const tick = prefNoAltTick.value
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (tick === 0) return
    setVisible(true)
    const timer = setTimeout(() => setVisible(false), 3000)
    return () => clearTimeout(timer)
  }, [tick])
  if (!visible) return null
  return <p class="climb-noalt">{tr('connector.no_alternative')}</p>
}
